import React, {ReactNode} from 'react';

import {darken, lighten} from 'polished';
import {useTranslation} from 'react-i18next';
import SelectComponent, {Props as SelectProps, OptionProps, components} from 'react-select';
import {makeAsyncSelect} from 'react-select/async';
import ACS from 'react-select/async-creatable';
import Creatable from 'react-select/creatable';
import styled, {css} from 'styled-components';

import {ReactComponent as ArrowButtonIcon} from 'assets/svg/arrow-bottom.svg';
import {ReactComponent as PlusIcon} from 'assets/svg/plus.svg';
import {border, colors, inputFieldBase, InputFieldBaseType, textEllipsis} from 'theme';

import {Button, ButtonVariant} from '../Button';
import {Icon} from '../Icon';
import {Label} from '../Label';

export interface SelectOption<A> {
    label: ReactNode;
    value: A | null;
}

export type SelectVariant = 'default' | 'checkbox';

interface StyledSelectProps {
    isDisabled?: boolean;
    hasError?: boolean;
    size?: 'sm' | 'md';
    $variant?: SelectVariant;
}

const DropdownIndicator = (props: any) => (
    <components.DropdownIndicator {...props}>
        <Icon source={ArrowButtonIcon} width={10} height={6} />
    </components.DropdownIndicator>
);

const CheckboxOption = (props: OptionProps<any, boolean>) => {
    const {isSelected, label} = props;
    return (
        <components.Option {...props}>
            <span className="react-select__option-label">{label}</span>
            <span
                className={`react-select__option-checkbox ${
                    isSelected ? 'react-select__option-checkbox--checked' : ''
                }`}
                aria-hidden
            >
                {isSelected && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                            d="M1 4L4 6.5L9 1.5"
                            stroke="#FFFFFF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
            </span>
        </components.Option>
    );
};

interface Props extends SelectProps {
    createButtonIcon?: ReactNode;
    createButtonLabel?: ReactNode;
    label?: string;
    hasError?: boolean;
    isCreateButtonVisible?: (inputValue: string) => boolean;
    disabled?: boolean;
    formFieldName?: string;
    onCreateButtonClick?: (inputValue: string) => void;
    optionComponent?: (props: OptionProps<any, false>) => JSX.Element;
    size?: 'sm' | 'md';
    variant?: SelectVariant;
}

export const Select = React.forwardRef<SelectComponent, Props>((props, ref) => {
    const {t} = useTranslation();
    const {variant = 'default', optionComponent, components: userComponents, ...rest} = props;
    const Option = optionComponent ?? (variant === 'checkbox' ? CheckboxOption : undefined);

    return (
        <Label text={props.label} hasError={props.hasError} isRequired={props.isRequired}>
            <CustomSelectComponent
                classNamePrefix="react-select"
                placeholder={t('components.select.chooseOption')}
                loadingMessage={() => `${t('components.select.Loading')}'...'`}
                noOptionsMessage={() => t('components.select.NotFound')}
                tabSelectsValue={false}
                isDisabled={props.disabled}
                openMenuOnFocus
                ref={ref}
                hideSelectedOptions={false}
                $variant={variant}
                {...rest}
                components={{
                    DropdownIndicator,
                    ...(Option && {Option}),
                    ...userComponents,
                }}
            />
        </Label>
    );
});

export const AsyncSelect = makeAsyncSelect(Select);

export const CreatableSelect = React.forwardRef<SelectComponent, Props>((props, ref) => {
    const {t} = useTranslation();
    const {variant = 'default', optionComponent, components: userComponents, ...rest} = props;
    const Option = optionComponent ?? (variant === 'checkbox' ? CheckboxOption : undefined);

    return (
        <Label text={props.label} hasError={props.hasError} isRequired={props.isRequired}>
            <CustomCreatableComponent
                classNamePrefix="react-select"
                placeholder={t('components.select.chooseOption')}
                loadingMessage={() => `${t('components.select.Loading')}'...'`}
                noOptionsMessage={() => t('components.select.NotFound')}
                tabSelectsValue={false}
                isDisabled={props.disabled}
                openMenuOnFocus
                ref={ref}
                $variant={variant}
                {...rest}
                components={{
                    DropdownIndicator,
                    ...(Option && {Option}),
                    ...userComponents,
                }}
            />
        </Label>
    );
});

const CustomAsyncCreatableComponent = styled(ACS)<StyledSelectProps>`
    ${(p) =>
        getStyles(p.isDisabled ? 'disabled' : p.hasError ? 'error' : 'normal', p.size, p.$variant)};
`;

const Menu = (props: any) => {
    const {t} = useTranslation();
    const inputValue = props.selectProps.inputValue?.trim() ?? '';
    const shouldShowCreateButton =
        !!props.selectProps.onCreateButtonClick &&
        (props.selectProps.isCreateButtonVisible?.(inputValue) ?? !!inputValue);

    return (
        <components.Menu {...props}>
            {props.children}
            {shouldShowCreateButton && (
                <CreateButtonWrapper onMouseDown={(event) => event.preventDefault()}>
                    <Button
                        type="button"
                        variant={ButtonVariant.Normal}
                        size="sm"
                        style={{width: 'fit-content'}}
                        icon={
                            props.selectProps.createButtonIcon ?? (
                                <Icon source={PlusIcon} width={12} height={12} fillColor="white" />
                            )
                        }
                        onClick={() => props.selectProps.onCreateButtonClick(inputValue)}
                    >
                        {props.selectProps.createButtonLabel ?? t('components.select.Create')}
                    </Button>
                </CreateButtonWrapper>
            )}
        </components.Menu>
    );
};

export const AsyncCreatableSelect = React.forwardRef<SelectComponent, Props>((props, ref) => {
    const {t} = useTranslation();
    const {variant = 'default', optionComponent, components: userComponents, ...rest} = props;
    const Option = optionComponent ?? (variant === 'checkbox' ? CheckboxOption : undefined);

    return (
        <Label text={props.label} hasError={props.hasError} isRequired={props.isRequired}>
            <CustomAsyncCreatableComponent
                classNamePrefix="react-select"
                placeholder={props.placeholder || t('components.select.chooseOption')}
                loadingMessage={() => t('components.select.Loading')}
                noOptionsMessage={() => t('components.select.NotFound')}
                formatCreateLabel={(inputValue: string) => (
                    <div className="react-select__create-label">
                        <span className="plus-icon">+</span> {t('components.select.Create')}
                        {`"${inputValue}"`}
                    </div>
                )}
                isValidNewOption={props.onCreateButtonClick ? () => false : undefined}
                tabSelectsValue={false}
                isDisabled={props.disabled}
                openMenuOnFocus
                ref={ref}
                $variant={variant}
                {...rest}
                components={{
                    DropdownIndicator,
                    Menu,
                    ...(Option && {Option}),
                    ...userComponents,
                }}
            />
        </Label>
    );
});

const checkboxVariantStyles = css`
    .react-select {
        &__menu {
            border: 1px solid #e7e8ea;
            border-radius: 12px;
            box-shadow: none;
            overflow: hidden;
        }

        &__menu-list {
            display: flex;
            flex-direction: column;
            gap: 2px;
            padding: 10px;
        }

        &__option {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: 4px;
            padding: 10px 12px;
            min-height: 38px;
            border: none;
            border-radius: 4px;
            background: #ffffff;
            color: #222222;
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 18px;
            cursor: pointer;
        }

        &__option:last-child {
            border: none;
        }

        &__option--is-selected,
        &__option--is-selected.react-select__option--is-focused {
            background: #f6f7ff;
            color: #222222;
        }

        &__option--is-focused:not(.react-select__option--is-selected) {
            background: #fafbff;
            color: #222222;
        }

        &__option-label {
            flex: 1 1 auto;
            min-width: 0;
        }

        &__option-checkbox {
            flex: 0 0 16px;
            width: 16px;
            height: 16px;
            border-radius: 4px;
            background: #ffffff;
            box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(70, 79, 96, 0.16);
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }

        &__option-checkbox--checked {
            background: #6466e9;
            box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1), 0 0 0 1px #6466e9;
        }
    }
`;

const getStyles = (type: InputFieldBaseType, size?: 'sm' | 'md', variant?: SelectVariant) => css`
    width: 100%;

    .react-select {
        &__control {
            ${textEllipsis};
            ${inputFieldBase(type, size)};
            cursor: pointer;
            min-height: ${size === 'sm' ? '36px' : '44px'};
            padding: ${size === 'sm' ? '2px 6px' : '6px 12px'};
            ${size === 'sm' &&
                css`
                    height: 30px;
                `}

            &--is-focused {
                box-shadow: none;
            }

            &--is-disabled {
                background: #f7f7f7;
                .react-select__dropdown-indicator {
                    color: ${colors.textMuted};
                }
                .react-select__placeholder {
                    color: ${colors.darkGrey};
                }
            }
        }

        &__single-value--is-disabled {
            color: ${colors.darkGrey};
        }

        &__placeholder {
            color: ${colors.textSecondary};
        }
        &__value-container {
            padding: ${size === 'sm' ? '2px 6px' : 0};

            > :last-child {
                margin: 0;
                padding: 0;
            }

            &--is-multi {
                width: 0;
                height: 100%;
                flex-wrap: nowrap;
                gap: ${size === 'sm' ? '3px' : '5px'};
            }
        }

        &__dropdown-indicator {
            color: ${colors.textPrimary};
            padding: 0;

            &:hover {
                color: ${colors.textPrimary};
            }

            svg {
                height: 14px;
            }
        }

        &__clear-indicator {
            color: ${colors.textPrimary};
            padding: 0;
            svg {
                height: ${size === 'sm' ? '12px' : '14px'};
            }
        }

        &__indicator-separator {
            display: none;
        }

        &__menu {
            margin: 0;
            &-list {
                padding: 4px;
                max-height: 360px;
            }
        }

        &__multi-value {
            display: flex;
            align-items: center;
            height: 100%;
            overflow: hidden;
            margin: 0;
            border-radius: ${border.radius};
            color: ${colors.brandPrimary};
            background: ${colors.brandLight};

            &__label {
                color: ${colors.brandPrimary} !important;
                font-size: 14px;
                font-weight: 600;
            }

            &__remove {
                border-radius: 0 ${border.radius} ${border.radius} 0;
                transition: all 0.15s ease-in-out;
                height: 100%;

                & > svg {
                    width: 15px;
                    height: 20px;
                    fill: ${colors.brandPrimary};
                }

                &:hover,
                &:focus {
                    background: ${colors.brandPrimary};
                    color: ${colors.brandLight};
                    & > svg {
                        fill: ${colors.brandLight};
                    }
                }
            }
        }

        &__option {
            cursor: pointer;
            padding: ${size === 'sm' ? '6px 10px' : '10px 15px'};
            font-size: 12px;
            background-color: ${colors.white};
            border-radius: ${border.radius};
            border-bottom: 1px solid ${colors.border};
            color: ${colors.textPrimary};

            &:last-child {
                border: 0;
            }

            &--is-selected {
                color: ${colors.white};
                background-color: ${colors.brandPrimary};
            }

            &--is-focused {
                &:not(.react-select__option--is-selected) {
                    background-color: ${lighten(0.35, colors.brandPrimary)};
                }
                &.react-select__option--is-selected {
                    background-color: ${darken(0.05, colors.brandPrimary)};
                }
            }
        }
    }

    ${variant === 'checkbox' && checkboxVariantStyles};
`;

const CustomSelectComponent = styled(SelectComponent)<StyledSelectProps>`
    ${(p) =>
        getStyles(p.isDisabled ? 'disabled' : p.hasError ? 'error' : 'normal', p.size, p.$variant)};
`;

const CustomCreatableComponent = styled(Creatable)<StyledSelectProps>`
    ${(p) =>
        getStyles(p.isDisabled ? 'disabled' : p.hasError ? 'error' : 'normal', p.size, p.$variant)};
`;

const CreateButtonWrapper = styled.div`
    padding: 10px 6px 6px;
    border-top: 1px solid ${colors.border};
`;

// const CreatableSelectWrapper = React.forwardRef<any, any>((props, ref) => {
//     const {t} = useTranslation();
//     return (
//         <Creatable
//             classNamePrefix="react-select"
//             placeholder={t('components.select.chooseOption')}
//             tabSelectsValue={false}
//             openMenuOnFocus
//             ref={ref}
//             {...props}
//         />
//     );
// });
//
// export const CreatableSelect = styled(CreatableSelectWrapper)<{hasError?: boolean}>`
//     ${styles};
//
//     ${(p) =>
//         p.hasError &&
//         css`
//             .react-select {
//                 &__control {
//                     border-color: ${colors.danger};
//
//                     &:hover {
//                         border-color: ${colors.danger};
//                     }
//
//                     &--is-focused {
//                         border-color: ${colors.danger};
//
//                         &:hover {
//                             border-color: ${colors.danger};
//                         }
//                     }
//                 }
//             }
//         `};
// `;
